import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import taskFormService from './taskForm.service';
import moment from 'moment';
import toLower from 'lodash/toLower';
import { extractPriorityFromDescription } from '../tasks.utils';
import size from 'lodash/size';

/**
 * Custom hook for task form logic (add/edit)
 *
 * Parent
 *      ImprovedTaskForm
 */
export const useImprovedTaskForm = ({
  isOpen,
  onClose,
  onSuccess,
  selectedTask = null,
  users = [],
}) => {
  const isEditMode = size(selectedTask) > 0;

  // Initialize form with react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
    control,
  } = useForm({
    defaultValues: {
      taskName: '',
      comment: '',
      taskType: '',
      status: '',
      ownerId: '',
      dueDate: '',
      priority: 'Medium',
    },
  });

  // Watch all form values to determine if submit should be enabled
  const watchedValues = useWatch({ control });

  // Reset form when modal opens/closes or selectedTask changes
  const resetForm = useCallback(() => {
    if (isEditMode && selectedTask) {
      // Add a small timeout to ensure modal DOM is ready
      const timeout = setTimeout(() => {
        if (selectedTask?.version === '1') {
          setValue(
            'priority',
            toLower(extractPriorityFromDescription(selectedTask.task_desc))
          );
        } else {
          setValue('priority', selectedTask?.priority || 'Medium');
        }
        setValue('taskName', selectedTask.task_title || '');
        setValue('comment', selectedTask?.comment || '');
        setValue(
          'dueDate',
          selectedTask.due_on
            ? moment(selectedTask.due_on, 'YYYY-MM-DD').format('MM/DD/YYYY')
            : ''
        );

        // Find and set the owner
        const ownerUser = users.find(
          u => u.label === selectedTask.assignedUser?.first_name
        );
        setValue('ownerId', selectedTask?.assigned_to_id || '');
        setValue('taskType', selectedTask.task_type || 'general');

        // Set status for edit mode
        setValue(
          'status',
          selectedTask.status === 'Not yet Started'
            ? 'todo'
            : selectedTask.status || 'Not yet Started'
        );
      }, 50);

      return timeout;
    } else {
      // Reset form for add mode
      reset({
        taskName: '',
        comment: '',
        taskType: '',
        status: 'todo', // Default status for new tasks
        ownerId: users.length > 0 ? users[0].value : '',
        dueDate: '',
        priority: 'Medium',
      });
      return null;
    }
  }, [isEditMode, selectedTask, users, setValue, reset]);

  // Handle form submission
  const onSubmit = useCallback(
    async data => {
      try {
        if (isEditMode && selectedTask) {
          // Update existing task
          await taskFormService.modifyTask({
            version: selectedTask?.version || 1,
            taskId: selectedTask.id,
            taskName: data.taskName,
            comment: data.comment,
            ownerId: data.ownerId,
            dueDate: data.dueDate,
            status: data.status,
            priority: data.priority,
          });
        } else {
          // Add new task
          await taskFormService.addTask({
            version: 2,
            taskName: data.taskName,
            comment: data.comment,
            ownerId: data.ownerId,
            dueDate: data.dueDate,
            status: data.status,
            priority: data.priority || 'Medium', // Default to Medium if not provided
          });
        }

        // Call success callback to refresh data and close modal
        if (onSuccess) {
          onSuccess();
        }

        // Close modal
        onClose();
      } catch (error) {
        console.error('Error submitting task form:', error);
      }
    },
    [isEditMode, selectedTask, onSuccess, onClose]
  );

  // Handle modal close
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // Check if all required fields have values
  const isFormValid = useMemo(() => {
    const requiredFields = [
      'taskName',
      'taskType',
      'priority',
      'status',
      'ownerId',
      'dueDate',
    ];

    return requiredFields.every(field => {
      const value = watchedValues[field];
      return value && value.toString().trim() !== '';
    });
  }, [watchedValues]);

  const userOptions = useMemo(() => {
    return users.map(user => ({
      label: user.first_name,
      value: user.user_id_id,
    }));
  }, [users]);

  return {
    // Form state
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isSubmitting,
    watch,
    isFormValid,
    control,
    userOptions,

    // Modal state
    isEditMode,

    // Functions
    resetForm,
    handleClose,
  };
};

export default useImprovedTaskForm;
